import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adminAPI, APIError } from "../lib/api";
import type { OrganizationDetailResponse, SetOverrideRequest } from "../types/api";
import { TierBadge, StatusBadge, RoleBadge } from "../components/Badge";
import { useKeyboard } from "../hooks/useKeyboard";
import styles from "./OrganizationDetail.module.css";

export function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OrganizationDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  const [maxSeats, setMaxSeats] = useState<string>("");
  const [maxTeams, setMaxTeams] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    async function fetchData() {
      if (!id) return;

      try {
        const response = await adminAPI.getOrganization(id);
        setData(response);

        if (response.enterprise_override) {
          setMaxSeats(response.enterprise_override.max_seats?.toString() || "");
          setMaxTeams(response.enterprise_override.max_teams?.toString() || "");
          setNotes(response.enterprise_override.notes || "");
        }
      } catch (err) {
        if (err instanceof APIError) {
          setError(err.message);
        } else {
          setError("Failed to load organization");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id]);

  useKeyboard({
    onBack: () => navigate("/organizations"),
    onEdit: () => setEditing(true),
    onEscape: () => setEditing(false),
    onSectionJump: (section) => {
      const el = sectionRefs.current[section - 1];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  });

  const handleSaveOverride = async () => {
    if (!id) return;

    setSaving(true);
    setWarnings([]);

    try {
      const request: SetOverrideRequest = {
        max_seats: maxSeats ? parseInt(maxSeats, 10) : null,
        max_teams: maxTeams ? parseInt(maxTeams, 10) : null,
        notes: notes || null,
      };

      const response = await adminAPI.setOverride(id, request);

      if (response.warnings.length > 0) {
        setWarnings(response.warnings);
      }

      setData((prev) =>
        prev
          ? {
              ...prev,
              enterprise_override: response.override,
            }
          : null,
      );

      setEditing(false);
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOverride = async () => {
    if (!id) return;

    const preview = await adminAPI.removeOverride(id, false);
    const confirmMsg = `Remove enterprise override? Tier defaults will apply:\n\nMax Seats: ${preview.tier_defaults.maxSeats === Infinity ? "Unlimited" : preview.tier_defaults.maxSeats}\nMax Teams: ${preview.tier_defaults.maxTeams === Infinity ? "Unlimited" : preview.tier_defaults.maxTeams}`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await adminAPI.removeOverride(id, true);
      setData((prev) =>
        prev
          ? {
              ...prev,
              enterprise_override: null,
            }
          : null,
      );
      setMaxSeats("");
      setMaxTeams("");
      setNotes("");
      setEditing(false);
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message);
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatEventType = (type: string) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getEventIcon = (type: string) => {
    if (type.includes("tier")) return styles.eventIconTier;
    if (type.includes("override")) return styles.eventIconOverride;
    if (type.includes("failed") || type.includes("canceled")) return styles.eventIconError;
    return styles.eventIconBilling;
  };

  if (loading) {
    return <div className={styles.loading}>Loading organization...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!data) {
    return null;
  }

  const {
    organization: org,
    members,
    enterprise_override,
    subscription_events,
    current_usage,
  } = data;

  return (
    <div className={styles.page}>
      <Link to="/organizations" className={styles.backLink}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to Organizations
      </Link>

      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{org.name}</h1>
          <span className={styles.slug}>{org.slug}</span>
          <div className={styles.badges}>
            <TierBadge tier={org.subscription_tier} />
            <StatusBadge status={org.subscription_status} />
          </div>
        </div>

        {org.stripe_customer_id && (
          <div className={styles.headerActions}>
            <a
              href={`https://dashboard.stripe.com/customers/${org.stripe_customer_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.stripeLink}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Stripe Customer
            </a>
          </div>
        )}
      </header>

      <div className={styles.sections}>
        <section
          className={styles.section}
          ref={(el) => {
            sectionRefs.current[0] = el;
          }}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>1</span>
              Basic Info
            </h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Organization ID</span>
                <span className={`${styles.infoValue} ${styles.infoValueMono}`}>{org.id}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Owner Email</span>
                <span className={styles.infoValue}>{org.owner_email || "-"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Created</span>
                <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                  {formatDate(org.created_at)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Updated</span>
                <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                  {formatDate(org.updated_at)}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          className={styles.section}
          ref={(el) => {
            sectionRefs.current[1] = el;
          }}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>2</span>
              Subscription
            </h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Tier</span>
                <TierBadge tier={org.subscription_tier} />
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status</span>
                <StatusBadge status={org.subscription_status} />
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Period Start</span>
                <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                  {formatDate(org.current_period_start)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Period End</span>
                <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                  {formatDate(org.current_period_end)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Cancel at Period End</span>
                <span className={styles.infoValue}>{org.cancel_at_period_end ? "Yes" : "No"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Stripe Subscription ID</span>
                <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                  {org.stripe_subscription_id || "-"}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          className={styles.section}
          ref={(el) => {
            sectionRefs.current[2] = el;
          }}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>3</span>
              Members ({members.length})
            </h2>
          </div>
          <div className={styles.sectionContent}>
            <table className={styles.memberTable}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.user_id}>
                    <td>
                      <span
                        className={member.email_verified ? styles.email : styles.emailUnverified}
                      >
                        {member.email}
                        {!member.email_verified && " (unverified)"}
                      </span>
                    </td>
                    <td>
                      <RoleBadge role={member.role} />
                    </td>
                    <td className={styles.infoValueMono}>{formatDate(member.joined_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section
          className={styles.section}
          ref={(el) => {
            sectionRefs.current[3] = el;
          }}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>4</span>
              Enterprise Overrides
            </h2>
            {!editing && (
              <button className={styles.btnSecondary} onClick={() => setEditing(true)}>
                {enterprise_override ? "Edit" : "Add Override"}
              </button>
            )}
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.usageInfo}>
              <div className={styles.usageItem}>
                <span className={styles.usageLabel}>Current Seats</span>
                <span className={styles.usageValue}>{current_usage.seats}</span>
              </div>
              <div className={styles.usageItem}>
                <span className={styles.usageLabel}>Current Teams</span>
                <span className={styles.usageValue}>{current_usage.teams}</span>
              </div>
            </div>

            {editing ? (
              <div className={styles.overrideForm}>
                {warnings.length > 0 && (
                  <div className={styles.warningList}>
                    {warnings.map((w, i) => (
                      <div key={i} className={styles.warningItem}>
                        {w}
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="maxSeats">
                      Max Seats
                    </label>
                    <input
                      id="maxSeats"
                      type="number"
                      className={styles.formInput}
                      value={maxSeats}
                      onChange={(e) => setMaxSeats(e.target.value)}
                      placeholder="Leave blank for tier default"
                      min="0"
                    />
                    <span className={styles.formHint}>Leave empty to use tier default</span>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="maxTeams">
                      Max Teams
                    </label>
                    <input
                      id="maxTeams"
                      type="number"
                      className={styles.formInput}
                      value={maxTeams}
                      onChange={(e) => setMaxTeams(e.target.value)}
                      placeholder="Leave blank for tier default"
                      min="0"
                    />
                    <span className={styles.formHint}>Leave empty to use tier default</span>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="internalNotes">
                    Internal Notes
                  </label>
                  <textarea
                    id="internalNotes"
                    className={styles.formTextarea}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this enterprise agreement..."
                  />
                  <span className={styles.formHint}>Supports markdown formatting</span>
                </div>

                <div className={styles.formActions}>
                  <button
                    className={styles.btnPrimary}
                    onClick={handleSaveOverride}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Override"}
                  </button>
                  <button className={styles.btnSecondary} onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  {enterprise_override && (
                    <button className={styles.btnDanger} onClick={handleRemoveOverride}>
                      Remove Override
                    </button>
                  )}
                </div>
              </div>
            ) : enterprise_override ? (
              <>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Max Seats</span>
                    <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                      {enterprise_override.max_seats ?? "Tier Default"}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Max Teams</span>
                    <span className={`${styles.infoValue} ${styles.infoValueMono}`}>
                      {enterprise_override.max_teams ?? "Tier Default"}
                    </span>
                  </div>
                </div>
                {enterprise_override.notes && (
                  <div className={styles.notesPreview}>{enterprise_override.notes}</div>
                )}
              </>
            ) : (
              <div className={styles.noOverride}>
                No enterprise override configured. Click "Add Override" to set custom limits.
              </div>
            )}
          </div>
        </section>

        <section
          className={styles.section}
          ref={(el) => {
            sectionRefs.current[4] = el;
          }}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>5</span>
              Subscription Timeline
            </h2>
          </div>
          <div className={styles.sectionContent}>
            {subscription_events.length === 0 ? (
              <div className={styles.noOverride}>No subscription events recorded.</div>
            ) : (
              <div className={styles.eventTimeline}>
                {subscription_events.map((event) => (
                  <div key={event.id} className={styles.event}>
                    <div className={`${styles.eventIcon} ${getEventIcon(event.event_type)}`}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className={styles.eventContent}>
                      <div className={styles.eventType}>{formatEventType(event.event_type)}</div>
                      <div className={styles.eventMeta}>
                        {event.actor_is_admin && (
                          <span className={styles.eventAdmin}>Admin action</span>
                        )}
                        <span className={styles.eventDate}>{formatDate(event.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
